import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../context/StoreContext';
import reportsService from '../services/reportsService';
import categoryService from '../services/categoryService';
import supplierService from '../services/supplierService';

const defaultSummary = {
  totalSkus: 0,
  totalUnits: 0,
  inventoryValue: 0,
  retailValue: 0,
  lowStockCount: 0,
  outOfStockCount: 0,
};

const currency = (value) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const StockReportPage = () => {
  const { activeStore } = useStore();
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: 'all',
    lowStockThreshold: '',
  });
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(defaultSummary);
  const [summaryIncluded, setSummaryIncluded] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
    hasNext: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [options, setOptions] = useState({ fastMode: true, includeSummary: false });

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await categoryService.fetchCategories();
        setCategories(data || []);
      } catch (err) {
        console.warn('Unable to load categories for stock report', err);
      }
    };
    const loadSuppliers = async () => {
      try {
        const response = await supplierService.fetchSuppliers({ limit: 200, compact: true });
        setSuppliers(response?.data || response || []);
      } catch (err) {
        console.warn('Unable to load suppliers for stock report', err);
      }
    };
    loadCategories();
    loadSuppliers();
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      if (!activeStore) return;
      setLoading(true);
      setError('');
      try {
        const response = await reportsService.getStockReport({
          storeId: activeStore._id,
          ...filters,
          page: filters.page,
          limit: filters.limit || 100,
          fastMode: options.fastMode ? 1 : 0,
          includeSummary: options.includeSummary ? 1 : 0,
        });
        setRows(response.data?.data || []);
        setSummary(response.data?.summary || defaultSummary);
        setSummaryIncluded(Boolean(response.data?.summaryIncluded));
        const payload = response.data?.pagination || {};
        const nextPagination = {
          page: payload.page || 1,
          limit: payload.limit || filters.limit || 100,
        };
        if (Object.prototype.hasOwnProperty.call(payload, 'total')) {
          nextPagination.total = payload.total;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'totalPages')) {
          nextPagination.totalPages = payload.totalPages;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'hasNext')) {
          nextPagination.hasNext = payload.hasNext;
        }
        setPagination(nextPagination);
      } catch (err) {
        console.error('Failed to load stock report', err);
        const isTimeout =
          err.code === 'ECONNABORTED' || String(err.message || '').toLowerCase().includes('timeout');
        setError(
          err.response?.data?.message ||
            (isTimeout
              ? 'Stock report request timed out. Check your API URL and network connection.'
              : 'Failed to load stock report.')
        );
        setRows([]);
        setSummary(defaultSummary);
        setSummaryIncluded(false);
        setPagination({ page: 1, limit: 100, total: 0, totalPages: 1, hasNext: false });
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [activeStore, filters, options]);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All statuses' },
      { value: 'in', label: 'In Stock' },
      { value: 'low', label: 'Low Stock' },
      { value: 'out', label: 'Out of Stock' },
      { value: 'over', label: 'Overstocked' },
    ],
    []
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (nextPage) => {
    const currentPage = filters.page || 1;
    const hasTotalPages = Number.isFinite(pagination.totalPages);
    if (nextPage < 1) return;
    if (hasTotalPages && nextPage > pagination.totalPages) return;
    if (!hasTotalPages && nextPage > currentPage && !pagination.hasNext) return;
    setFilters((prev) => ({ ...prev, page: nextPage }));
  };

  if (!activeStore) {
    return <div className="p-6 text-slate-600">Please select a store to view the stock report.</div>;
  }

  const summaryCards = [
    {
      label: 'SKUs in view',
      value: summaryIncluded ? summary.totalSkus.toLocaleString() : '—',
    },
    {
      label: 'Units on hand',
      value: summaryIncluded ? summary.totalUnits.toLocaleString() : '—',
    },
    {
      label: 'Inventory value (cost)',
      value: summaryIncluded ? currency(summary.inventoryValue) : '—',
    },
    {
      label: 'Sale value (retail)',
      value: summaryIncluded ? currency(summary.retailValue) : '—',
    },
  ];

  const exportColumns = [
    { key: 'sku', label: 'SKU' },
    { key: 'barcode', label: 'Barcode' },
    { key: 'name', label: 'Product' },
    { key: 'category', label: 'Category' },
    { key: 'supplierName', label: 'Supplier' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'unitCost', label: 'Unit Cost' },
    { key: 'unitPrice', label: 'Unit Price' },
    { key: 'inventoryValue', label: 'Inventory Value' },
    { key: 'retailValue', label: 'Sale Value' },
    { key: 'stockStatus', label: 'Status' },
  ];

  const buildExportRows = () =>
    rows.map((row) => ({
      sku: row.sku || '',
      barcode: row.barcode || '',
      name: row.name || '',
      category: row.category || '',
      supplierName: row.supplierName || '',
      quantity: row.quantity ?? 0,
      unitCost: Number(row.unitCost || 0).toFixed(2),
      unitPrice: Number(row.unitPrice || 0).toFixed(2),
      inventoryValue: Number(row.inventoryValue || 0).toFixed(2),
      retailValue: Number(row.retailValue || 0).toFixed(2),
      stockStatus:
        row.stockStatus === 'in'
          ? 'In Stock'
          : row.stockStatus === 'low'
          ? 'Low'
          : row.stockStatus === 'out'
          ? 'Out'
          : 'Overstock',
    }));

  const downloadCsv = () => {
    const rowsForExport = buildExportRows();
    const csv = [
      exportColumns.map((c) => c.label).join(','),
      ...rowsForExport.map((row) =>
        exportColumns
          .map((col) => {
            const value = row[col.key];
            const escaped = String(value ?? '').replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stock-report-${activeStore?.name || 'store'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadXls = () => {
    const rowsForExport = buildExportRows();
    const header = exportColumns.map((col) => `<th>${col.label}</th>`).join('');
    const body = rowsForExport
      .map(
        (row) =>
          `<tr>${exportColumns
            .map((col) => `<td>${row[col.key] ?? ''}</td>`)
            .join('')}</tr>`
      )
      .join('');
    const html = `
      <table border="1">
        <thead><tr>${header}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    `;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stock-report-${activeStore?.name || 'store'}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const statusBadgeClasses = {
    in: 'bg-emerald-100 text-emerald-700',
    low: 'bg-amber-100 text-amber-700',
    out: 'bg-rose-100 text-rose-700',
    over: 'bg-blue-100 text-blue-700',
  };

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-2xl shadow p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-slate-600 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Name, SKU, barcode"
              className="w-full border border-slate-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2"
            >
              <option value="">All categories</option>
              {categories.map((cat) => (
                <option key={cat._id || cat.name} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Supplier</label>
            <select
              value={filters.supplierId || ''}
              onChange={(e) => handleFilterChange('supplierId', e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2"
            >
              <option value="">All suppliers</option>
              {suppliers.map((supplier) => (
                <option key={supplier._id || supplier.id || supplier.name} value={supplier._id || supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Stock status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Low stock threshold (override)</label>
            <input
              type="number"
              min="0"
              value={filters.lowStockThreshold}
              onChange={(e) => handleFilterChange('lowStockThreshold', e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2"
              placeholder={
                activeStore?.lowStockThreshold != null
                  ? `Default: ${activeStore.lowStockThreshold}`
                  : 'Default: 5'
              }
            />
            <p className="mt-1 text-xs text-slate-500">Leave blank to use product/store defaults.</p>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <input
              id="fast-mode"
              type="checkbox"
              checked={options.fastMode}
              onChange={(e) =>
                setOptions((prev) => ({
                  ...prev,
                  fastMode: e.target.checked,
                  includeSummary: e.target.checked ? false : prev.includeSummary,
                }))
              }
              className="h-4 w-4 text-primary border-slate-300 rounded"
            />
            <label htmlFor="fast-mode" className="text-sm text-slate-600">
              Fast mode (skip summary)
            </label>
          </div>
        </div>
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2 rounded-md">
            {error}
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
          {summaryCards.map((card) => (
            <div key={card.label} className="bg-white rounded-2xl shadow p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">{card.label}</p>
              <p className="text-2xl font-semibold text-slate-900 mt-2">{card.value}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          {!summaryIncluded && (
            <button
              onClick={() =>
                setOptions((prev) => ({
                  ...prev,
                  includeSummary: true,
                  fastMode: false,
                }))
              }
              className="px-4 py-2 rounded-full border border-primary text-primary text-sm font-semibold hover:bg-primary hover:text-white"
            >
              Load summary
            </button>
          )}
          <button
            onClick={downloadCsv}
            className="px-4 py-2 rounded-full bg-slate-800 text-white text-sm font-semibold hover:bg-slate-700"
          >
            Export CSV
          </button>
          <button
            onClick={downloadXls}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
          >
            Export XLS
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
        <span>
          Low stock SKUs: <strong>{summaryIncluded ? summary.lowStockCount : '—'}</strong>
        </span>
        <span>
          Out of stock SKUs: <strong>{summaryIncluded ? summary.outOfStockCount : '—'}</strong>
        </span>
      </div>

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Barcode
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Product</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Supplier</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Unit Cost</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Unit Price</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Inventory Value</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Sale Value</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-6 text-center text-slate-500">
                    Loading stock report...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-6 text-center text-slate-500">
                    No inventory records found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.productId}>
                    <td className="px-4 py-2 text-sm text-slate-700 font-mono">{row.sku || '—'}</td>
                    <td className="px-4 py-2 text-sm text-slate-700 font-mono">{row.barcode || '—'}</td>
                    <td className="px-4 py-2 text-sm text-slate-900">{row.name}</td>
                    <td className="px-4 py-2 text-sm text-slate-600">{row.category || '—'}</td>
                    <td className="px-4 py-2 text-sm text-slate-600">{row.supplierName || '—'}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-900">{row.quantity?.toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-900">{currency(row.unitCost)}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-900">{currency(row.unitPrice)}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-900">{currency(row.inventoryValue)}</td>
                    <td className="px-4 py-2 text-sm text-right text-slate-900">{currency(row.retailValue)}</td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                          statusBadgeClasses[row.stockStatus] || 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {row.stockStatus === 'in'
                          ? 'In Stock'
                          : row.stockStatus === 'low'
                          ? 'Low'
                          : row.stockStatus === 'out'
                          ? 'Out'
                          : 'Over'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center px-4 py-3 border-t text-sm text-slate-600">
          {(() => {
            const page = pagination.page || 1;
            const hasTotalPages = Number.isFinite(pagination.totalPages);
            const totalPages = hasTotalPages ? pagination.totalPages : undefined;
            const totalItems = hasTotalPages ? pagination.total || 0 : rows.length;
            const nextDisabled = hasTotalPages ? page >= totalPages : !pagination.hasNext;
            return (
              <>
                <button
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={(filters.page || 1) <= 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span>
                  {hasTotalPages
                    ? `Page ${page} of ${totalPages} · ${totalItems} items`
                    : `Page ${page}${pagination.hasNext ? ' · more available' : ''} · ${totalItems} items shown`}
                </span>
                <button
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={nextDisabled}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default StockReportPage;
